'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { AuthTrigger } from './AuthTrigger';
import { useAuth } from '@/lib/auth';

const NAV_LINKS = [
  { href: '/trends', label: 'ИИ-тренды' },
  { href: '/blog', label: 'Блог' },
  { href: '/faq', label: 'Вопросы' },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const { authed, ready } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className="sticky top-0 z-50 w-full transition-colors duration-300"
      style={{ backgroundColor: scrolled ? '#1B1C1E' : '#000000' }}
    >
      <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between gap-8 px-5 md:h-[90px] md:px-10">
        <Link href="/" aria-label="banana" className="inline-flex items-center">
          <Image
            src="/logo-wordmark.svg"
            alt="banana"
            width={100}
            height={66}
            priority
            className="h-[42px] w-auto md:h-[54px]"
          />
        </Link>

        <nav className="hidden flex-1 items-center gap-2 md:flex">
          {NAV_LINKS.map((link) => {
            const active = link.href.startsWith('/') && !link.href.startsWith('/#') && pathname.startsWith(link.href);
            return (
              <Link
                key={link.href}
                href={link.href}
                className={`px-2.5 py-2.5 text-base transition-colors ${
                  active ? 'text-[#ffa207]' : 'text-white/90 hover:text-white'
                }`}
                style={{ letterSpacing: '-0.4px' }}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          {ready && authed ? (
            <Link
              href="/app"
              className="hidden h-12 items-center justify-center rounded-[20px] bg-[#ef6444] px-6 text-sm font-semibold text-white transition-colors hover:bg-[#d3573b] md:inline-flex"
            >
              В кабинет
            </Link>
          ) : (
            <AuthTrigger className="hidden h-12 items-center justify-center rounded-[20px] bg-[#ef6444] px-6 text-sm font-semibold text-white transition-colors hover:bg-[#d3573b] md:inline-flex">
              Войти
            </AuthTrigger>
          )}
          <button
            type="button"
            aria-label="Меню"
            onClick={() => setOpen((value) => !value)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-white md:hidden"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="border-t border-white/5 px-5 pb-6 pt-4 md:hidden"
          style={{ backgroundColor: scrolled ? '#1B1C1E' : '#000000' }}
        >
          <nav className="flex flex-col gap-4">
            {NAV_LINKS.map((link) => {
              const active = link.href.startsWith('/') && !link.href.startsWith('/#') && pathname.startsWith(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className={`text-base font-medium ${active ? 'text-[#ffa207]' : 'text-white/90'}`}
                >
                  {link.label}
                </Link>
              );
            })}
            {ready && authed ? (
              <Link
                href="/app"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex h-12 items-center justify-center rounded-[20px] bg-[#ef6444] px-5 text-base font-semibold text-white"
              >
                В кабинет
              </Link>
            ) : (
              <AuthTrigger
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex h-12 items-center justify-center rounded-[20px] bg-[#ef6444] px-5 text-base font-semibold text-white"
              >
                Войти
              </AuthTrigger>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
